/**
 * Form helpers for visio-background create and edit views.
 */
import type {
  CreateVisioBackgroundFormState,
  EditVisioBackgroundFormState,
  UpdateVisioBackgroundDto,
  VisioBackground,
} from '@/types/visio-background.types';

/** Empty create form. */
export function emptyCreateVisioBackgroundForm(): CreateVisioBackgroundFormState {
  return { name: '', file: null, fileRejected: false };
}

/** Empty edit form before GET hydrates it. */
export function emptyEditVisioBackgroundForm(): EditVisioBackgroundFormState {
  return { name: '', file: null, hasExistingImage: false, fileRejected: false };
}

/** GET entity → edit form fields. Replacement file stays empty until the user picks one. */
export function visioBackgroundToForm(background: VisioBackground): EditVisioBackgroundFormState {
  return { name: background.name, file: null, hasExistingImage: true, fileRejected: false };
}

/** File basename without the last extension. */
function defaultNameFromFile(file: File): string {
  return file.name.replace(/\.[^.]+$/, '');
}

/** Keep a typed name; otherwise seed from the selected file. */
export function applyVisioBackgroundFileSelect(
  form: CreateVisioBackgroundFormState,
  file: File | null,
): CreateVisioBackgroundFormState {
  if (!file) return { ...form, file: null, fileRejected: false };
  return {
    file,
    name: form.name.trim() ? form.name : defaultNameFromFile(file),
    fileRejected: false,
  };
}

/** Patch the display name on create or edit. */
export function applyVisioBackgroundName<T extends { name: string }>(form: T, name: string): T {
  return { ...form, name };
}

/** Replace the image on edit without touching the typed name. */
export function applyVisioBackgroundEditFile(
  form: EditVisioBackgroundFormState,
  file: File | null,
): EditVisioBackgroundFormState {
  return { ...form, file, fileRejected: false };
}

/** Edit form → PATCH body. */
export function buildUpdateVisioBackgroundDto(
  form: EditVisioBackgroundFormState,
): UpdateVisioBackgroundDto {
  return { name: form.name.trim() };
}
